import type { DrawingToolInstance, ChartLayout, TimestampToX, PriceToY, DraggingOverride } from "./types";
import { clipToChart } from "./drawHelpers";
import { drawHorizontalLine } from "./drawHorizontalLine";
import { drawVerticalLine } from "./drawVerticalLine";
import { drawTrendLine } from "./drawTrendLine";
import { drawRayLine } from "./drawRayLine";
import { drawFibonacci } from "./drawFibonacci";
import { drawRectangle } from "./drawRectangle";
import { drawParallelChannel } from "./drawParallelChannel";

export const drawDrawingInstances = (
  ctx: CanvasRenderingContext2D,
  instances: DrawingToolInstance[],
  layout: ChartLayout,
  timestampToX: TimestampToX,
  priceToY: PriceToY,
  draggingOverride?: DraggingOverride | null,
): void => {
  if (instances.length === 0) return;

  ctx.save();
  clipToChart(ctx, layout);

  for (const instance of instances) {
    const { coordinates, name, fibonacci } = instance;
    if (!coordinates) continue;
    ctx.save();

    if (name === "horizontalLine" && coordinates.kind === "single-price") {
      const draggingPrice = draggingOverride?.kind === "price" && draggingOverride.id === instance.id
        ? draggingOverride.price : undefined;
      drawHorizontalLine(ctx, coordinates, instance, layout, priceToY, draggingPrice);

    } else if (name === "verticalLine" && coordinates.kind === "single-timestamp") {
      const draggingTimestamp = draggingOverride?.kind === "timestamp" && draggingOverride.id === instance.id
        ? draggingOverride.timestamp : undefined;
      drawVerticalLine(ctx, coordinates, instance, layout, timestampToX, draggingTimestamp);

    } else if (name === "trendLine" && coordinates.kind === "two-point") {
      const draggingCoords = draggingOverride?.kind === "twoPoint" && draggingOverride.id === instance.id
        ? { kind: "two-point" as const, point1: draggingOverride.point1, point2: draggingOverride.point2 } : undefined;
      drawTrendLine(ctx, coordinates, instance, timestampToX, priceToY, draggingCoords);

    } else if (name === "rayLine" && coordinates.kind === "two-point") {
      const draggingCoords = draggingOverride?.kind === "twoPoint" && draggingOverride.id === instance.id
        ? { kind: "two-point" as const, point1: draggingOverride.point1, point2: draggingOverride.point2 } : undefined;
      drawRayLine(ctx, coordinates, instance, timestampToX, priceToY, draggingCoords);

    } else if (name === "fibonacciRetracement" && coordinates.kind === "two-point") {
      const draggingCoords = draggingOverride?.kind === "twoPoint" && draggingOverride.id === instance.id
        ? { kind: "two-point" as const, point1: draggingOverride.point1, point2: draggingOverride.point2 } : undefined;
      drawFibonacci(ctx, coordinates, instance, layout, timestampToX, priceToY, fibonacci, draggingCoords);

    } else if (name === "rectangle" && coordinates.kind === "two-point") {
      const draggingCoords = draggingOverride?.kind === "twoPoint" && draggingOverride.id === instance.id
        ? { kind: "two-point" as const, point1: draggingOverride.point1, point2: draggingOverride.point2 } : undefined;
      drawRectangle(ctx, coordinates, instance, timestampToX, priceToY, draggingCoords);

    } else if (name === "parallelChannel" && coordinates.kind === "three-point") {
      const draggingCoords = draggingOverride?.kind === "threePoint" && draggingOverride.id === instance.id
        ? { kind: "three-point" as const, point1: draggingOverride.point1, point2: draggingOverride.point2, point3: draggingOverride.point3 } : undefined;
      drawParallelChannel(ctx, coordinates, instance, timestampToX, priceToY, draggingCoords);
    }

    ctx.restore();
  }

  ctx.restore();
};
